import React from 'react';
import { BarChart } from '../../components/ui/BarChart';
import { DashboardIcon, DashboardIconType } from '../../components/ui/DashboardIcon';
import { DetailList } from '../../components/ui/DetailList';
import { EmptyState } from '../../components/ui/EmptyState';
import { FleetMetricCard } from '../../components/ui/FleetMetricCard';
import { IconButton } from '../../components/ui/IconButton';
import { ModalShell } from '../../components/ui/ModalShell';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { StatusPill } from '../../components/ui/StatusPill';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import {
  AdminActivityItem,
  AdminOverviewResponse,
  AdminPendingAction,
  AdminPendingActionTotals,
  AdminSignup,
  getAdminOverview,
} from '../../services/admin';

type StatCard = {
  value: string;
  label: string;
  icon: DashboardIconType;
  tone: string;
};

type ActivityItem = {
  day: string;
  value: number;
  newUsers: number;
  walletTransactions: number;
};

type PendingAction = {
  title: string;
  body: string;
  count: number;
  tone: string;
  icon: DashboardIconType;
};

type Signup = {
  id: string;
  initials: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  status: string;
};

const defaultPendingActions: PendingAction[] = [
  { title: 'KYC reviews', body: '4 driver applications', count: 4, tone: 'gold', icon: 'shield' },
  { title: 'SOS incidents', body: '2 active reports', count: 2, tone: 'red', icon: 'alert' },
  { title: 'Refund requests', body: '3 pending review', count: 3, tone: 'blue', icon: 'card' },
];

export function AdminOverview() {
  const [overview, setOverview] = React.useState<AdminOverviewResponse | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [selectedSignup, setSelectedSignup] = React.useState<Signup | null>(null);
  const closeSignup = React.useCallback(() => setSelectedSignup(null), []);

  React.useEffect(() => {
    let isActive = true;

    getAdminOverview()
      .then((data) => {
        if (isActive) {
          setOverview(data);
        }
      })
      .catch((requestError) => {
        if (isActive) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load admin overview');
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEscapeKey(Boolean(selectedSignup), closeSignup);

  if (isLoading) {
    return (
      <section className="fleet-panel empty">
        <EmptyState title="Loading overview" message="Fetching the latest admin dashboard metrics." />
      </section>
    );
  }

  if (error) {
    return (
      <section className="fleet-panel empty">
        <EmptyState title="Unable to load overview" message={error} />
      </section>
    );
  }

  const adminStats = getStatCards(overview);
  const platformActivity = getPlatformActivity(overview);
  const pendingActions = getPendingActions(overview);
  const recentSignups = getRecentSignups(overview);

  return (
    <div className="admin-dashboard">
      <section className="fleet-stat-grid admin-stat-grid">
        {adminStats.map((stat) => (
          <FleetMetricCard
            className="admin-stat-card"
            icon={stat.icon}
            key={stat.label}
            label={stat.label}
            tone={stat.tone}
            value={stat.value}
          />
        ))}
      </section>

      <section className="fleet-main-grid admin-main-grid">
        <Panel>
          <PanelHeader
            actions={<button className="btn" type="button">Last 7 days</button>}
            title="Platform activity"
          />
          <BarChart
            ariaLabel="Platform activity chart"
            className="fleet-bars admin-bars"
            items={platformActivity.map((item) => ({
              label: item.day,
              value: item.value,
              title: `${item.newUsers} new users, ${item.walletTransactions} wallet transactions`,
            }))}
          />
        </Panel>

        <aside className="fleet-panel">
          <PanelHeader title="Pending actions" />
          <div className="fleet-alert-list admin-actions">
            {pendingActions.map((action) => (
              <article className="fleet-alert admin-action" key={action.title}>
                <span className={`fleet-icon ${action.tone}`}>
                  <DashboardIcon type={action.icon} size={24} />
                </span>
                <div>
                  <strong>{action.title}</strong>
                  <span>{action.body}</span>
                </div>
                <em>{action.count}</em>
              </article>
            ))}
          </div>
        </aside>
      </section>

      <Panel className="fleet-panel admin-signups">
        <PanelHeader title="Recent signups" />
        <div className="admin-signup-list">
          {recentSignups.length ? recentSignups.map((signup) => (
            <article className="admin-signup" key={signup.id}>
              <span className="row-avatar">{signup.initials}</span>
              <div>
                <strong>{signup.name}</strong>
                <span>{signup.email} {signup.phone}</span>
                <StatusPill tone={signup.status === 'active' ? 'success' : 'muted'}>{signup.status}</StatusPill>
                <StatusPill tone="info">{signup.role}</StatusPill>
              </div>
              <IconButton
                ariaLabel={`View ${signup.name}`}
                icon="eye"
                onClick={() => setSelectedSignup(signup)}
              />
            </article>
          )) : (
            <EmptyState title="No recent signups" message="New users will appear here when they join the platform." />
          )}
        </div>
      </Panel>

      {selectedSignup ? (
        <ModalShell
          labelledBy="signup-details-title"
          onClose={closeSignup}
          title={(
            <>
              <div>
                <span className="row-avatar">{selectedSignup.initials}</span>
                <div>
                  <h2 id="signup-details-title">{selectedSignup.name}</h2>
                  <StatusPill tone="info">{selectedSignup.role}</StatusPill>
                </div>
              </div>
            </>
          )}
        >
          <DetailList
            className="signup-details"
            items={[
              { label: 'Email', value: selectedSignup.email || 'Not provided' },
              { label: 'Phone', value: selectedSignup.phone || 'Not provided' },
              {
                label: 'Account status',
                value: <StatusPill tone={selectedSignup.status === 'active' ? 'success' : 'muted'}>{selectedSignup.status}</StatusPill>,
              },
              { label: 'User ID', value: selectedSignup.id },
            ]}
          />
        </ModalShell>
      ) : null}
    </div>
  );
}

function getStatCards(overview: AdminOverviewResponse | null): StatCard[] {
  const stats = overview?.stats;

  return [
    {
      value: formatNumber(overview?.users_total ?? stats?.totalUsers ?? stats?.total_users),
      label: 'Total users',
      icon: 'users',
      tone: 'blue',
    },
    { value: formatNumber(overview?.passengers_total), label: 'Passengers', icon: 'passenger', tone: 'green' },
    {
      value: formatNumber(overview?.drivers_total ?? stats?.activeDrivers ?? stats?.active_drivers),
      label: 'Drivers',
      icon: 'driver',
      tone: 'gold',
    },
    { value: formatNumber(overview?.fleet_managers_total), label: 'Fleet managers', icon: 'shield', tone: 'blue' },
    {
      value: formatNumber(overview?.vehicles_total ?? stats?.vehiclesOnPlatform ?? stats?.vehicles_on_platform),
      label: 'Vehicles',
      icon: 'vehicle',
      tone: 'green',
    },
    { value: formatNumber(overview?.assignments_total), label: 'Assignments', icon: 'assignment', tone: 'gold' },
    { value: formatNumber(overview?.open_sos_incidents_total), label: 'Open SOS incidents', icon: 'alert', tone: 'red' },
    { value: formatNumber(overview?.wallet_transactions_total), label: 'Wallet transactions', icon: 'wallet', tone: 'blue' },
    {
      value: formatNumber(overview?.wallet_transactions_today_total ?? stats?.transactionsToday ?? stats?.transactions_today),
      label: 'Transactions today',
      icon: 'card',
      tone: 'green',
    },
  ];
}

function getPlatformActivity(overview: AdminOverviewResponse | null): ActivityItem[] {
  const activity = overview?.platformActivity ?? overview?.platform_activity ?? [];

  return activity.map((item: AdminActivityItem, index) => ({
    day: item.day ?? item.label ?? formatActivityDate(item.activity_date) ?? `Day ${index + 1}`,
    value: item.activity_total ?? item.value ?? item.count ?? 0,
    newUsers: item.new_users_total ?? 0,
    walletTransactions: item.wallet_transactions_total ?? 0,
  }));
}

function getPendingActions(overview: AdminOverviewResponse | null): PendingAction[] {
  const actions = overview?.pendingActions ?? overview?.pending_actions ?? [];

  if (!Array.isArray(actions)) {
    return getPendingActionTotals(actions);
  }

  if (!actions.length) {
    return defaultPendingActions;
  }

  return actions.map((action: AdminPendingAction) => ({
    title: action.title ?? 'Pending action',
    body: action.body ?? action.description ?? 'Review required',
    count: action.count ?? 0,
    tone: getSupportedTone(action.tone),
    icon: getSupportedIcon(action.icon),
  }));
}

function getPendingActionTotals(actions: AdminPendingActionTotals): PendingAction[] {
  const kycReviews = actions.kyc_reviews_total ?? 0;
  const sosIncidents = actions.open_sos_incidents_total ?? 0;
  const refundRequests = actions.refund_requests_total ?? 0;

  return [
    {
      title: 'KYC reviews',
      body: `${formatNumber(kycReviews)} driver ${kycReviews === 1 ? 'application' : 'applications'}`,
      count: kycReviews,
      tone: 'gold',
      icon: 'shield',
    },
    {
      title: 'SOS incidents',
      body: `${formatNumber(sosIncidents)} active ${sosIncidents === 1 ? 'report' : 'reports'}`,
      count: sosIncidents,
      tone: 'red',
      icon: 'alert',
    },
    {
      title: 'Refund requests',
      body: `${formatNumber(refundRequests)} pending review`,
      count: refundRequests,
      tone: 'blue',
      icon: 'card',
    },
  ];
}

function getRecentSignups(overview: AdminOverviewResponse | null): Signup[] {
  const signups = overview?.recentSignups ?? overview?.recent_signups ?? [];

  return signups.map((signup: AdminSignup, index) => {
    const name = signup.name ?? signup.fullName ?? signup.full_name ?? signup.email ?? 'New user';

    return {
      id: signup.id ?? signup.email ?? String(index),
      initials: signup.initials ?? getInitials(name),
      name,
      email: signup.email ?? '',
      phone: signup.phone ?? signup.phone_number ?? '',
      role: signup.requested_role ?? signup.role ?? 'passenger',
      status: signup.status ?? (signup.is_active === false ? 'inactive' : 'active'),
    };
  });
}

function formatNumber(value: number | undefined) {
  return (value ?? 0).toLocaleString();
}

function getSupportedTone(tone: string | undefined) {
  if (tone === 'gold' || tone === 'red' || tone === 'blue' || tone === 'green') {
    return tone;
  }

  return 'blue';
}

function getSupportedIcon(icon: string | undefined) {
  if (icon === 'shield' || icon === 'alert' || icon === 'card') {
    return icon;
  }

  return 'shield';
}

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'NU';
}

function formatActivityDate(date: string | undefined) {
  if (!date) {
    return undefined;
  }

  const parsedDate = new Date(`${date}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
  });
}
